import React, { useState, useEffect } from 'react';
import { Form, Input, Switch, Button, Card, message, Tabs, Spin, Space, DatePicker, Select, Upload } from 'antd';
import { UploadOutlined, PictureOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';
import dayjs from 'dayjs';
import RichTextEditor from '../../components/common/RichTextEditor';
import MediaPickerModal from '../../components/common/MediaPickerModal';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';

const { RangePicker } = DatePicker;

export const PosterEdit: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const isEditMode = !!id;
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [form] = Form.useForm();
    const [mediaPickerOpen, setMediaPickerOpen] = useState(false);

    const uploadImageMutation = useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('isPublic', 'true');
            formData.append('isActive', 'true');

            const response = await apiClient.post('/admin/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        },
        onSuccess: (data: any) => {
            message.success('Image uploaded successfully.');
            const dataObj = data?.data || data?.Data || data;
            const imgUrl = dataObj?.url || dataObj?.Url || dataObj?.filePath || dataObj?.FilePath || `/api/media/download/${data.id || data.Id}`;
            form.setFieldsValue({ imageUrl: imgUrl });
        },
        onError: (error: any) => {
            message.error(error.response?.data?.detail || 'Image upload failed.');
        }
    });

    const handleImageUpload = (file: File) => {
        uploadImageMutation.mutate(file);
        return false;
    };

    const appLanguages = ['de', 'en', 'tr', 'ru', 'ar'];

    const { data: posterData, isLoading } = useQuery({
        queryKey: ['adminPoster', id],
        queryFn: async () => {
            const response = await apiClient.get(`/admin/posters/${id}`);
            return response.data?.data || response.data?.Data || response.data;
        },
        enabled: isEditMode
    });

    const initialValues = React.useMemo(() => {
        if (!posterData) {
            return { isActive: true, category: 'general' };
        }
        const values: any = {
            imageUrl: posterData.imageUrl || posterData.ImageUrl || '',
            redirectUrl: posterData.redirectUrl || posterData.RedirectUrl || posterData.pageUrl || posterData.PageUrl || '',
            category: posterData.category || posterData.Category || 'general',
            isActive: posterData.isActive ?? posterData.IsActive ?? true,
        };

        const startDate = posterData.publishStartDate || posterData.PublishStartDate;
        const endDate = posterData.publishEndDate || posterData.PublishEndDate;
        if (startDate || endDate) {
            values.publishPeriod = [
                startDate ? dayjs(startDate) : null,
                endDate ? dayjs(endDate) : null,
            ];
        }
        const evDate = posterData.eventDate || posterData.EventDate;
        if (evDate) {
            values.eventDate = dayjs(evDate);
        }

        const translations = posterData.translations || posterData.Translations || [];
        appLanguages.forEach(lang => {
            const trans = translations.find((t: any) =>
                (t.languageCode || t.LanguageCode)?.toLowerCase() === lang.toLowerCase()
            );
            values[`translations_${lang}`] = trans ? {
                title: trans.title || trans.Title || '',
                description: trans.description || trans.Description || ''
            } : {
                title: '',
                description: ''
            };
        });

        return values;
    }, [posterData]);

    useEffect(() => {
        if (posterData) {
            const formValues: any = {
                imageUrl: posterData.imageUrl || posterData.ImageUrl || '',
                redirectUrl: posterData.redirectUrl || posterData.RedirectUrl || posterData.pageUrl || posterData.PageUrl || '',
                category: posterData.category || posterData.Category || 'general',
                isActive: posterData.isActive ?? posterData.IsActive ?? true,
            };

            const startDate = posterData.publishStartDate || posterData.PublishStartDate;
            const endDate = posterData.publishEndDate || posterData.PublishEndDate;
            if (startDate || endDate) {
                formValues.publishPeriod = [
                    startDate ? dayjs(startDate) : null,
                    endDate ? dayjs(endDate) : null,
                ];
            }
            const evDate = posterData.eventDate || posterData.EventDate;
            if (evDate) {
                formValues.eventDate = dayjs(evDate);
            }

            const translations = posterData.translations || posterData.Translations || [];
            appLanguages.forEach(lang => {
                const trans = translations.find((t: any) =>
                    (t.languageCode || t.LanguageCode)?.toLowerCase() === lang.toLowerCase()
                );
                formValues[`translations_${lang}`] = trans ? {
                    title: trans.title || trans.Title || '',
                    description: trans.description || trans.Description || ''
                } : {
                    title: '',
                    description: ''
                };
            });

            setTimeout(() => {
                form.setFieldsValue(formValues);
            }, 0);
        } else if (!isEditMode) {
            form.setFieldsValue({ isActive: true, category: 'general' });
        }
    }, [posterData, form, isEditMode]);

    const saveMutation = useMutation({
        mutationFn: async (values: any) => {
            const translations = appLanguages.map(lang => {
                const t = values[`translations_${lang}`];
                
                // If form values exist and have a title, use them
                if (t && t.title) {
                    return {
                        languageCode: lang,
                        title: t.title,
                        description: t.description || null
                    };
                }

                // Fallback to original database values if the tab/fields were never mounted/submitted (undefined)
                const originalTranslations = posterData?.translations || posterData?.Translations || [];
                const originalTrans = originalTranslations.find((x: any) => 
                    (x.languageCode || x.LanguageCode)?.toLowerCase() === lang.toLowerCase()
                );
                if (originalTrans && t === undefined) {
                    return {
                        languageCode: lang,
                        title: originalTrans.title || originalTrans.Title,
                        description: originalTrans.description || originalTrans.Description || null
                    };
                }

                return null;
            }).filter(Boolean);

            if (translations.length === 0) {
                throw new Error("At least one language translation with a Title is required.");
            }

            const publishStartDate = values.publishPeriod?.[0] ? values.publishPeriod[0].toISOString() : null;
            const publishEndDate = values.publishPeriod?.[1] ? values.publishPeriod[1].toISOString() : null;
            const eventDate = values.eventDate ? values.eventDate.toISOString() : null;

            const payload = {
                id: isEditMode ? id : undefined,
                imageUrl: values.imageUrl,
                redirectUrl: values.redirectUrl || null,
                pageUrl: values.redirectUrl || null, // API handles backend URL based on this
                category: values.category,
                isActive: values.isActive,
                publishStartDate,
                publishEndDate,
                eventDate,
                translations
            };

            if (isEditMode) {
                await apiClient.put(`/admin/posters/${id}`, payload);
            } else {
                await apiClient.post('/admin/posters', payload);
            }
        },
        onSuccess: () => {
            message.success(`Poster ${isEditMode ? 'updated' : 'created'} successfully.`);
            queryClient.invalidateQueries({ queryKey: ['adminPosters'] });
            navigate('/admin/posters');
        },
        onError: (error: any) => {
            message.error(error.response?.data?.detail || error.message || 'Failed to save poster.');
        }
    });

    if (isEditMode && isLoading) {
        return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
    }

    const tabItems = appLanguages.map(lang => ({
        key: lang,
        label: lang.toUpperCase(),
        forceRender: true,
        children: (
            <div style={{ display: 'block', width: '100%' }}>
                <Form.Item name={[`translations_${lang}`, 'title']} label="Title">
                    <Input placeholder={`Title in ${lang.toUpperCase()}`} />
                </Form.Item>
                <Form.Item name={[`translations_${lang}`, 'description']} label="Description">
                    <RichTextEditor
                        key={`editor_${lang}_${posterData?.id || 'new'}`}
                        placeholder={`Description in ${lang.toUpperCase()}`}
                        height={240}
                    />
                </Form.Item>
            </div>
        )
    }));

    return (
        <Card title={isEditMode ? 'Edit Poster' : 'Create New Poster'}>
            <Form 
                key={posterData?.id || (isEditMode ? 'loading' : 'new')}
                form={form} 
                initialValues={initialValues}
                layout="vertical" 
                onFinish={(values) => saveMutation.mutate(values)}
            >

                {/* Core Properties */}
                <div style={{ marginBottom: 24, width: '100%' }}>
                    <Form.Item label="Image URL (Internal/External)" required>
                        <Space.Compact style={{ width: '100%' }}>
                            <Form.Item 
                                name="imageUrl" 
                                noStyle 
                                rules={[{ required: true, message: 'Image URL is required' }]}
                            >
                                <Input placeholder="e.g. /api/media/download/123e4567..." />
                            </Form.Item>
                            <Upload
                                beforeUpload={handleImageUpload}
                                showUploadList={false}
                                maxCount={1}
                            >
                                <Button icon={<UploadOutlined />} loading={uploadImageMutation.isPending}>
                                    Upload
                                </Button>
                            </Upload>
                            <Button 
                                icon={<PictureOutlined />}
                                onClick={() => setMediaPickerOpen(true)}
                            >
                                Mediathek
                            </Button>
                        </Space.Compact>
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev?.imageUrl !== curr?.imageUrl}>
                        {({ getFieldValue }) => {
                            const url = getFieldValue('imageUrl');
                            if (!url) return null;
                            return (
                                <div style={{ marginBottom: 16 }}>
                                    <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Vorschau / Preview:</div>
                                    <img 
                                        src={resolveAssetUrl(url)} 
                                        alt="Poster Vorschau" 
                                        style={{ maxHeight: 160, maxWidth: 240, objectFit: 'contain', border: '1px solid #d9d9d9', padding: 4, borderRadius: 8, background: '#fafafa' }} 
                                    />
                                </div>
                            );
                        }}
                    </Form.Item>
                    <Form.Item name="redirectUrl" label="Redirect Link URL (Optional)">
                        <Input placeholder="e.g. https://example.com/tickets" />
                    </Form.Item>

                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                        <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                            <Select style={{ width: 180 }}>
                                <Select.Option value="general">Genel (General)</Select.Option>
                                <Select.Option value="youth">Gençlik (Youth)</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item name="isActive" label="Status" valuePropName="checked">
                            <Switch checkedChildren="Active" unCheckedChildren="Draft" />
                        </Form.Item>
                        <Form.Item name="eventDate" label="Event Date">
                            <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: 200 }} />
                        </Form.Item>
                        <Form.Item name="publishPeriod" label="Publish Visibility Period (Start - End)">
                            <RangePicker showTime format="YYYY-MM-DD HH:mm" />
                        </Form.Item>
                    </div>
                </div>

                {/* Translation Properties */}
                <Card type="inner" title="Translations" style={{ marginBottom: 24 }}>
                    <Tabs type="card" items={tabItems} />
                </Card>

                <Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit" loading={saveMutation.isPending}>
                            Save Poster
                        </Button>
                        <Button onClick={() => navigate('/admin/posters')}>Cancel</Button>
                    </Space>
                </Form.Item>
            </Form>

            <MediaPickerModal
                open={mediaPickerOpen}
                onCancel={() => setMediaPickerOpen(false)}
                onSelect={(url) => {
                    form.setFieldsValue({ imageUrl: url });
                    setMediaPickerOpen(false);
                }}
            />
        </Card>
    );
};
